import { useState, type CSSProperties, type ReactNode } from 'react';
import type { Layer } from '@geosoft/viewer-sdk';
import { Icon, tokens } from '@/design';
import { isVectorToolCardLayer } from '../vector/vectorStyleModel';
import type { TableFramePropertiesPatch } from './layoutDocument';
import { LayoutTextSymbolPanel } from './LayoutTextSymbolPanel';
import { SymbolColorPicker } from './SymbolColorPicker';
import {
  TABLE_FRAME_STYLES,
  tableFrameStyleMeta,
} from './tableFrameStyles';
import type {
  MapFrameElement,
  TableFieldAlignment,
  TableFieldSortDirection,
  TableFrameElement,
  TableFrameFittingStrategy,
  TableFrameQuery,
  TableFrameRuntimeData,
} from './layoutTypes';

export interface TableFramePropertiesPanelProps {
  readonly tableFrame: TableFrameElement;
  readonly frames: readonly MapFrameElement[];
  readonly layers: readonly Layer[];
  readonly data: TableFrameRuntimeData | null;
  readonly onChange: (patch: TableFramePropertiesPatch) => void;
  readonly onDelete: () => void;
  readonly onClose: () => void;
}

type View = 'options' | 'display' | 'fields' | 'headingText' | 'rowText';

function Section({
  title,
  children,
  open = false,
}: {
  readonly title: string;
  readonly children: ReactNode;
  readonly open?: boolean;
}) {
  return (
    <details open={open}>
      <summary
        style={{
          minHeight: tokens.density.rowDense,
          cursor: 'pointer',
          fontWeight: tokens.font.weight.semibold,
          fontSize: tokens.font.size.md,
        }}
      >
        {title}
      </summary>
      <div
        style={{
          display: 'grid',
          gap: tokens.space[2],
          padding: `0 0 ${tokens.space[3]}px ${tokens.space[5]}px`,
        }}
      >
        {children}
      </div>
    </details>
  );
}

function bounded(
  value: string,
  min: number,
  max: number,
  fallback: number,
): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(min, Math.min(max, parsed)) : fallback;
}

export function TableFramePropertiesPanel({
  tableFrame,
  frames,
  layers,
  data,
  onChange,
  onDelete,
  onClose,
}: TableFramePropertiesPanelProps) {
  const [view, setView] = useState<View>('options');
  const [fieldName, setFieldName] = useState(tableFrame.fields[0]?.name ?? '');
  const field =
    tableFrame.fields.find((candidate) => candidate.name === fieldName)
    ?? tableFrame.fields[0]
    ?? null;
  const tableLayers = layers.filter(isVectorToolCardLayer);

  const input: CSSProperties = {
    width: '100%',
    minHeight: tokens.density.rowDense,
    boxSizing: 'border-box',
    padding: `0 ${tokens.space[2]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[0],
    color: tokens.color.ink.primary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  };
  const row: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `${tokens.density.touchMin * 2.8}px minmax(0, 1fr)`,
    alignItems: 'center',
    gap: tokens.space[2],
    fontSize: tokens.font.size.sm,
  };
  const check: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.space[2],
    fontSize: tokens.font.size.sm,
  };
  const iconButton: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: tokens.density.rowDense,
    minHeight: tokens.density.rowDense,
    padding: 0,
    border: 0,
    background: 'transparent',
    color: tokens.color.ink.secondary,
    cursor: 'pointer',
  };
  const tab = (active: boolean): CSSProperties => ({
    minHeight: tokens.density.rowDefault,
    padding: `0 ${tokens.space[1]}px`,
    border: 0,
    borderBottom: `${tokens.focus.ringWidth}px solid ${
      active ? tokens.color.semantic.info : 'transparent'
    }`,
    background: 'transparent',
    color: active ? tokens.color.semantic.info : tokens.color.ink.secondary,
    cursor: 'pointer',
    fontFamily: tokens.font.family.ui,
  });
  const numberField = (
    label: string,
    value: number,
    min: number,
    max: number,
    update: (value: number) => void,
  ) => (
    <label style={row}>
      <span>{label}</span>
      <input
        aria-label={label}
        type="number"
        min={min}
        max={max}
        step={0.5}
        value={value}
        onChange={(event) =>
          update(bounded(event.target.value, min, max, value))
        }
        style={input}
      />
    </label>
  );
  const colorField = (
    label: string,
    value: string,
    update: (value: string) => void,
  ) => (
    <div style={row}>
      <span>{label}</span>
      <SymbolColorPicker
        label={label}
        value={value}
        allowNoColor
        onChange={(color) => update(color ?? 'transparent')}
      />
    </div>
  );
  const patchField = (patch: Parameters<typeof onChange>[0]['field']) => {
    if (field) onChange({ fieldName: field.name, field: patch });
  };

  if (view === 'headingText' || view === 'rowText') {
    const heading = view === 'headingText';
    return (
      <aside
        data-testid="table-frame-properties-panel"
        aria-label="Table frame properties"
        style={{
          position: 'absolute',
          zIndex: tokens.zIndex.dock,
          top: tokens.space[3],
          right: tokens.space[3],
          width: tokens.layout.popoverLg,
          maxHeight: `calc(100% - ${tokens.space[6]}px)`,
          overflowY: 'auto',
          padding: tokens.space[3],
          border: `${tokens.focus.ringWidth}px solid ${tokens.color.semantic.info}`,
          borderRadius: tokens.radius.lg,
          background: tokens.color.surface[1],
          boxShadow: tokens.elevation[2],
        }}
      >
        <LayoutTextSymbolPanel
          value={heading ? tableFrame.headingTextSymbol : tableFrame.rowTextSymbol}
          heading={heading ? 'Heading' : 'Rows'}
          testId="table-frame-text-symbol-panel"
          onApply={(symbol) =>
            onChange(
              heading
                ? { headingTextSymbol: symbol }
                : { rowTextSymbol: symbol },
            )
          }
          onBack={() => setView('display')}
        />
      </aside>
    );
  }

  return (
    <aside
      data-testid="table-frame-properties-panel"
      aria-label="Table frame properties"
      style={{
        position: 'absolute',
        zIndex: tokens.zIndex.dock,
        top: tokens.space[3],
        right: tokens.space[3],
        width: tokens.layout.popoverLg,
        maxHeight: `calc(100% - ${tokens.space[6]}px)`,
        overflowY: 'auto',
        padding: tokens.space[3],
        border: `${tokens.focus.ringWidth}px solid ${tokens.color.semantic.info}`,
        borderRadius: tokens.radius.lg,
        background: tokens.color.surface[1],
        color: tokens.color.ink.primary,
        boxShadow: tokens.elevation[2],
        fontFamily: tokens.font.family.ui,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <div style={{ color: tokens.color.semantic.info, fontSize: tokens.font.size.lg }}>
            Element
          </div>
          <strong>{tableFrame.name}</strong>
        </div>
        <div style={{ display: 'flex' }}>
          <button
            type="button"
            aria-label="Delete table frame"
            disabled={tableFrame.locked}
            onClick={onDelete}
            style={iconButton}
          >
            <Icon name="delete" size={20} decorative />
          </button>
          <button
            type="button"
            aria-label="Close table frame properties"
            onClick={onClose}
            style={iconButton}
          >
            <Icon name="close" size={20} decorative />
          </button>
        </div>
      </div>

      <div
        role="tablist"
        aria-label="Table frame property tabs"
        style={{ display: 'flex', gap: tokens.space[3], marginTop: tokens.space[2] }}
      >
        {(['options', 'display', 'fields'] as const).map((candidate) => (
          <button
            key={candidate}
            type="button"
            role="tab"
            aria-selected={view === candidate}
            onClick={() => setView(candidate)}
            style={tab(view === candidate)}
          >
            {candidate === 'options'
              ? 'Options'
              : candidate === 'display'
                ? 'Display'
                : 'Fields'}
          </button>
        ))}
      </div>

      {view === 'options' ? (
        <div role="tabpanel" aria-label="Table frame options">
          <Section title="General" open>
            <label style={row}>
              <span>Name</span>
              <input
                aria-label="Table frame name"
                value={tableFrame.name}
                onChange={(event) => onChange({ name: event.target.value })}
                style={input}
              />
            </label>
            <label style={check}>
              <input
                type="checkbox"
                aria-label="Table frame visible"
                checked={tableFrame.visible}
                onChange={(event) => onChange({ visible: event.target.checked })}
              />
              Visible
            </label>
            <label style={check}>
              <input
                type="checkbox"
                aria-label="Table frame locked"
                checked={tableFrame.locked}
                onChange={(event) => onChange({ locked: event.target.checked })}
              />
              Locked
            </label>
          </Section>
          <Section title="Source" open>
            <label style={row}>
              <span>Map Frame</span>
              <select
                aria-label="Table frame map frame"
                value={tableFrame.mapFrameId}
                onChange={(event) => onChange({ mapFrameId: event.target.value })}
                style={input}
              >
                {frames.map((frame, index) => (
                  <option key={frame.id} value={frame.id}>
                    {frames.length === 1 ? 'Map Frame' : `Map Frame ${index + 1}`}
                  </option>
                ))}
              </select>
            </label>
            <label style={row}>
              <span>Table</span>
              <select
                aria-label="Table frame source layer"
                value={tableFrame.sourceLayerId ?? ''}
                onChange={(event) =>
                  onChange({
                    sourceLayerId: event.target.value || null,
                    fields: [],
                  })
                }
                style={input}
              >
                <option value="">None</option>
                {tableLayers.map((layer) => (
                  <option key={layer.id} value={layer.id}>
                    {layer.label ?? layer.id}
                  </option>
                ))}
              </select>
            </label>
            <span role="status" style={{ fontSize: tokens.font.size.xs }}>
              {data?.loading
                ? 'Loading records…'
                : data?.error
                  ? data.error
                  : `${data?.rows.length ?? 0} records`}
            </span>
            <label style={row}>
              <span>Query</span>
              <select
                aria-label="Table frame query"
                value={tableFrame.query}
                onChange={(event) =>
                  onChange({ query: event.target.value as TableFrameQuery })
                }
                style={input}
              >
                <option value="all">All rows</option>
                <option value="visibleExtent" disabled>
                  Features visible in map extent
                </option>
                <option value="selected" disabled>
                  Selected features
                </option>
              </select>
            </label>
            {numberField(
              'Table frame maximum rows',
              tableFrame.maximumRows,
              1,
              10000,
              (maximumRows) => onChange({ maximumRows: Math.round(maximumRows) }),
            )}
          </Section>
          <Section title="Fitting Strategy" open>
            <label style={row}>
              <span>Strategy</span>
              <select
                aria-label="Table frame fitting strategy"
                value={tableFrame.fittingStrategy}
                onChange={(event) =>
                  onChange({
                    fittingStrategy:
                      event.target.value as TableFrameFittingStrategy,
                  })
                }
                style={input}
              >
                <option value="adjustColumns">Adjust columns</option>
                <option value="adjustFontSize">Adjust font size</option>
                <option value="adjustColumnsAndFont">
                  Adjust columns and font size
                </option>
                <option value="adjustFrame">Adjust frame</option>
              </select>
            </label>
            {numberField(
              'Table frame minimum font size',
              tableFrame.minimumFontSizePt,
              4,
              72,
              (minimumFontSizePt) => onChange({ minimumFontSizePt }),
            )}
            <label style={check}>
              <input
                type="checkbox"
                aria-label="Balance table frame columns"
                checked={tableFrame.balanceColumns}
                onChange={(event) =>
                  onChange({ balanceColumns: event.target.checked })
                }
              />
              Balance columns
            </label>
            {numberField(
              'Table frame column gap',
              tableFrame.frameColumnGapPt,
              0,
              144,
              (frameColumnGapPt) => onChange({ frameColumnGapPt }),
            )}
          </Section>
          <Section title="Style">
            <label style={row}>
              <span>Style</span>
              <select
                aria-label="Table frame style"
                value={tableFrame.styleId}
                onChange={(event) => {
                  const style = tableFrameStyleMeta(
                    event.target.value as TableFrameElement['styleId'],
                  );
                  onChange({
                    styleId: style.id,
                    headingBackgroundColor: style.headingBackground,
                    headingBorderColor: style.headingBorder,
                    headingUnderlineColor: style.headingUnderline,
                    rowBorderColor: style.rowBorder,
                    rowBackground1: style.rowBackground1,
                    rowBackground2: style.rowBackground2,
                    rowBackground1Count: style.background1Count,
                    rowBackground2Count: style.background2Count,
                    frame: {
                      outlineColor: style.frameBorder,
                      outlineWidthPt:
                        style.frameBorder === 'transparent' ? 0 : 1,
                    },
                    headingTextSymbol: {
                      color: style.headingColor,
                      fontStyle: style.boldHeading ? 'bold' : 'regular',
                    },
                  });
                }}
                style={input}
              >
                {TABLE_FRAME_STYLES.map((style) => (
                  <option key={style.id} value={style.id}>{style.label}</option>
                ))}
              </select>
            </label>
          </Section>
        </div>
      ) : null}

      {view === 'display' ? (
        <div role="tabpanel" aria-label="Table frame display">
          <Section title="Table" open>
            {colorField('Table frame background', tableFrame.frame.color, (color) =>
              onChange({ frame: { color, enabled: color !== 'transparent' } }),
            )}
            {colorField('Table frame border', tableFrame.frame.outlineColor, (outlineColor) =>
              onChange({ frame: { outlineColor } }),
            )}
            {numberField(
              'Table frame border width',
              tableFrame.frame.outlineWidthPt,
              0,
              12,
              (outlineWidthPt) => onChange({ frame: { outlineWidthPt } }),
            )}
          </Section>
          <Section title="Heading" open>
            <label style={check}>
              <input
                type="checkbox"
                aria-label="Show table frame heading"
                checked={tableFrame.showHeading}
                onChange={(event) => onChange({ showHeading: event.target.checked })}
              />
              Show heading
            </label>
            <button
              type="button"
              aria-label="Edit table frame heading text symbol"
              onClick={() => setView('headingText')}
              style={input}
            >
              Aa Heading text symbol
            </button>
            {colorField('Table heading background', tableFrame.headingBackgroundColor, (headingBackgroundColor) =>
              onChange({ headingBackgroundColor }),
            )}
            {colorField('Table heading border', tableFrame.headingBorderColor, (headingBorderColor) =>
              onChange({ headingBorderColor }),
            )}
            {colorField('Table heading underline', tableFrame.headingUnderlineColor, (headingUnderlineColor) =>
              onChange({ headingUnderlineColor }),
            )}
          </Section>
          <Section title="Rows" open>
            <button
              type="button"
              aria-label="Edit table frame row text symbol"
              onClick={() => setView('rowText')}
              style={input}
            >
              Aa Row text symbol
            </button>
            {colorField('Table row border', tableFrame.rowBorderColor, (rowBorderColor) =>
              onChange({ rowBorderColor }),
            )}
            {colorField('Table row background 1', tableFrame.rowBackground1, (rowBackground1) =>
              onChange({ rowBackground1 }),
            )}
            {numberField(
              'Table row background 1 count',
              tableFrame.rowBackground1Count,
              1,
              20,
              (rowBackground1Count) =>
                onChange({ rowBackground1Count: Math.round(rowBackground1Count) }),
            )}
            {colorField('Table row background 2', tableFrame.rowBackground2, (rowBackground2) =>
              onChange({ rowBackground2 }),
            )}
            {numberField(
              'Table row background 2 count',
              tableFrame.rowBackground2Count,
              1,
              20,
              (rowBackground2Count) =>
                onChange({ rowBackground2Count: Math.round(rowBackground2Count) }),
            )}
          </Section>
        </div>
      ) : null}

      {view === 'fields' ? (
        <div role="tabpanel" aria-label="Table frame fields">
          <label style={{ ...row, margin: `${tokens.space[3]}px 0` }}>
            <span>Field</span>
            <select
              aria-label="Selected table frame field"
              value={field?.name ?? ''}
              onChange={(event) => setFieldName(event.target.value)}
              style={input}
            >
              {tableFrame.fields.map((candidate) => (
                <option key={candidate.name} value={candidate.name}>
                  {candidate.alias}
                </option>
              ))}
            </select>
          </label>
          {field ? (
            <>
              <Section title="Field" open>
                <label style={check}>
                  <input
                    type="checkbox"
                    aria-label="Show table frame field"
                    checked={field.visible}
                    onChange={(event) =>
                      patchField({ visible: event.target.checked })
                    }
                  />
                  Show field
                </label>
                <label style={row}>
                  <span>Heading</span>
                  <input
                    aria-label="Table frame field heading"
                    value={field.alias}
                    onChange={(event) => patchField({ alias: event.target.value })}
                    style={input}
                  />
                </label>
                {numberField(
                  'Table frame field width',
                  field.widthPt,
                  12,
                  720,
                  (widthPt) => patchField({ widthPt }),
                )}
                {(['alignment', 'headingAlignment'] as const).map((key) => (
                  <label key={key} style={row}>
                    <span>{key === 'alignment' ? 'Values align' : 'Heading align'}</span>
                    <select
                      aria-label={`Table field ${key}`}
                      value={field[key]}
                      onChange={(event) =>
                        patchField({
                          [key]: event.target.value as TableFieldAlignment,
                        })
                      }
                      style={input}
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </label>
                ))}
                <label style={check}>
                  <input
                    type="checkbox"
                    aria-label="Wrap table field heading"
                    checked={field.wrapHeading}
                    onChange={(event) =>
                      patchField({ wrapHeading: event.target.checked })
                    }
                  />
                  Wrap heading
                </label>
                <label style={check}>
                  <input
                    type="checkbox"
                    aria-label="Wrap table field values"
                    checked={field.wrapValues}
                    onChange={(event) =>
                      patchField({ wrapValues: event.target.checked })
                    }
                  />
                  Wrap values
                </label>
              </Section>
              <Section title="Number Format">
                <label style={row}>
                  <span>Format</span>
                  <select
                    aria-label="Table field number format"
                    value={field.numberFormat}
                    onChange={(event) =>
                      patchField({
                        numberFormat:
                          event.target.value as typeof field.numberFormat,
                      })
                    }
                    style={input}
                  >
                    <option value="automatic">Automatic</option>
                    <option value="integer">Integer</option>
                    <option value="decimal">Decimal</option>
                    <option value="percent">Percent</option>
                  </select>
                </label>
                {numberField(
                  'Table field decimal places',
                  field.decimalPlaces,
                  0,
                  12,
                  (decimalPlaces) =>
                    patchField({ decimalPlaces: Math.round(decimalPlaces) }),
                )}
              </Section>
              <Section title="Sorting">
                <label style={row}>
                  <span>Direction</span>
                  <select
                    aria-label="Table field sort direction"
                    value={field.sortDirection}
                    onChange={(event) =>
                      patchField({
                        sortDirection:
                          event.target.value as TableFieldSortDirection,
                      })
                    }
                    style={input}
                  >
                    <option value="none">None</option>
                    <option value="ascending">Ascending</option>
                    <option value="descending">Descending</option>
                  </select>
                </label>
                {numberField(
                  'Table field sort order',
                  field.sortOrder,
                  0,
                  Math.max(0, tableFrame.fields.length - 1),
                  (sortOrder) => patchField({ sortOrder: Math.round(sortOrder) }),
                )}
              </Section>
              <Section title="Order">
                <div style={{ display: 'flex', gap: tokens.space[2] }}>
                  <button
                    type="button"
                    aria-label="Move table field left"
                    onClick={() => {
                      const index = tableFrame.fields.findIndex(
                        (candidate) => candidate.name === field.name,
                      );
                      if (index <= 0) return;
                      const fields = [...tableFrame.fields];
                      [fields[index - 1], fields[index]] = [
                        fields[index],
                        fields[index - 1],
                      ];
                      onChange({ fields });
                    }}
                    style={input}
                  >
                    Move left
                  </button>
                  <button
                    type="button"
                    aria-label="Move table field right"
                    onClick={() => {
                      const index = tableFrame.fields.findIndex(
                        (candidate) => candidate.name === field.name,
                      );
                      if (index < 0 || index >= tableFrame.fields.length - 1) return;
                      const fields = [...tableFrame.fields];
                      [fields[index], fields[index + 1]] = [
                        fields[index + 1],
                        fields[index],
                      ];
                      onChange({ fields });
                    }}
                    style={input}
                  >
                    Move right
                  </button>
                </div>
              </Section>
            </>
          ) : (
            <p>No fields are available for this layer.</p>
          )}
        </div>
      ) : null}
    </aside>
  );
}
